import { motion } from 'framer-motion';
import party from 'party-js';
import React, { useRef, useState } from 'react';

import axios from 'axios';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { useAlgo } from '../hooks/useAlgo';
import { useFlips } from '../hooks/useFlips';
import { useKittiesStore } from '../hooks/useKittiesStore';
import Alert from './Alert';
import Button from './Button';
import ConnectWalletMessage from './ConnectWalletMessage';
import FlipsModal from './FlipsModal';
import FlipsTokenDetail from './FlipsTokenDetail';
import Spinner from './Spinner';

function TheFlipsPage() {
  const confettiRef = useRef();
  const [modalOpen, setModalOpen] = useState(false);
  const { myKitties, myKittiesLoading } = useKittiesStore();
  const { fetchNewTotals, tokenChoices, betAmount, maxBet, setBetAmount, choiceOfToken, setChoiceOfToken, choiceToWin, setChoiceToWin, tokenTotals } =
    useFlips();
  const { address, sendBetForFlip } = useAlgo();
  const [success, setSuccess] = useState();
  const [coinFlipResult, setCoinFlipResult] = useState();
  const [fail, setFail] = useState();
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setCoinFlipResult(null);
    setModalOpen(false);
    setSuccess(null);
    setFail(null);
  };

  const onFlip = async () => {
    if (!address) return;

    if (!betAmount) {
      toast.error('Please enter a bet amount.');
      return;
    }

    const choice = tokenChoices.find((choice) => choice.id === choiceOfToken);
    if (betAmount > maxBet) {
      toast.error(`You can only bet up to ${maxBet} ${choice.name}.`);
      return;
    }

    try {
      setCoinFlipResult(null);
      setSuccess(null);
      setFail(null);
      setLoading(true);

      const totals = await fetchNewTotals();

      if (betAmount > totals.user) {
        toast.error(`You don't have enough ${choice.name} to make this bet.`);
        return;
      }

      if (betAmount > totals.house) {
        toast.error(`The house doesn't have enough ${choice.name} to make this bet.`);
        return;
      }

      setModalOpen(true);

      const txnId = await sendBetForFlip({ betAmount, choiceOfToken, choiceToWin });

      const { data } = await axios.post(
        '/api/flips/flip',
        {
          txnId,
        },
        {
          headers: {
            Authorization: `${process.env.NEXT_PUBLIC_API_KEY}`,
          },
        }
      );

      if (data.didWin) {
        party.confetti(confettiRef.current, {
          count: party.variation.range(20, 60),
        });
        party.confetti(confettiRef.current, {
          count: party.variation.range(20, 60),
        });
        party.confetti(confettiRef.current, {
          count: party.variation.range(20, 60),
        });
        setSuccess('🥳 You won! Check your wallet for your winnings.');
      } else {
        setFail('😿 You lost. Better luck next time.');
      }

      setCoinFlipResult(data.coinFlipResult);
    } catch (error) {
      reset();
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (process.env.NEXT_PUBLIC_FLIP_ENABLED !== 'true') {
    return (
      <div className='max-w-lg mx-auto mt-10 text-center'>
        <Alert type='danger' className={'flex text-center justify-center'}>
          Shitty Flips have been disabled. Please check back later.
        </Alert>
      </div>
    );
  }

  if (!address) {
    return (
      <div className='flex justify-center mt-20'>
        <ConnectWalletMessage />
      </div>
    );
  }

  if (myKittiesLoading) {
    return (
      <div className='flex justify-center mt-20'>
        <Spinner />
      </div>
    );
  }

  if (myKitties.length === 0) {
    return (
      <div className='max-w-lg mx-auto mt-10 text-center'>
        <Alert type='danger' className={'flex text-center justify-center'}>
          You must own a Shitty Kitty to participate in Shitty Flips.<br></br> Check out the{' '}
          <a href={`/marketplace`} className='font-bold'>
            Marketplace
          </a>{' '}
          to purchase a Shitty Kitty.
        </Alert>
      </div>
    );
  }

  return (
    <div className='flex flex-col items-center mt-10'>
      <FlipsTokenDetail />

      <div className='w-full md:w-auto flex flex-col md:flex-row items-center gap-2 mt-10'>
        <select
          value={choiceOfToken}
          onChange={(e) => setChoiceOfToken(parseInt(e.target.value))}
          placeholder='Bet Amount'
          className='w-full md:w-auto form-input bg-gray-900 border-0 rounded-md py-4 focus:ring-2 focus:ring-white pr-10 text-lg'
        >
          {tokenChoices.map((token) => (
            <option key={token.id} value={token.id}>
              {token.name}
            </option>
          ))}
        </select>

        <input
          type='number'
          value={betAmount}
          onChange={(e) => setBetAmount(parseInt(e.target.value))}
          placeholder='Bet Amount'
          className='w-full form-input bg-gray-900 border-0 rounded-md py-4 focus:ring-2 focus:ring-white md:w-[150px] text-lg'
        />
      </div>

      <div className='mt-10 w-full md:w-auto'>
        <button
          type='button'
          className='bg-gray-900 overflow-hidden relative w-1/2 md:w-[150px] h-[45px] rounded-l-lg'
          onClick={() => setChoiceToWin('heads')}
        >
          {choiceToWin === 'heads' && (
            <motion.div layout layoutId='active-tab' className='absolute z-10 inset-0 w-full h-full bg-primary-500'></motion.div>
          )}
          <span className={clsx('relative z-20  font-bold', { 'text-gray-900': choiceToWin === 'heads' })}>Heads</span>
        </button>
        <button
          type='button'
          className='bg-gray-900 overflow-hidden relative w-1/2 md:w-[150px] h-[45px] rounded-r-lg'
          onClick={() => setChoiceToWin('tails')}
        >
          {choiceToWin === 'tails' && (
            <motion.div layout layoutId='active-tab' className='absolute z-10 inset-0 w-full h-full bg-primary-500'></motion.div>
          )}
          <span className={clsx('relative z-20 font-bold', { 'text-gray-900': choiceToWin === 'tails' })}>Tails</span>
        </button>
      </div>

      <div className='mt-10 w-full md:w-auto'>
        <Button
          className='!w-full md:!w-auto text-center justify-center h-[45px] items-center'
          disabled={!betAmount || tokenTotals.user === 0 || myKitties.length === 0}
          onClick={onFlip}
        >
          {loading ? <Spinner dimensionClasses='w-6 h-6' /> : 'Flip a coin'}
        </Button>
        <div ref={confettiRef}></div>
      </div>

      <div className='text-center mt-10 text-gray-300'>
        <p>3% fee applied to flips. Check info below.</p>
      </div>

      <FlipsModal
        pending={loading}
        coinFlipResult={coinFlipResult}
        success={success}
        fail={fail}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}

export default TheFlipsPage;
